import { PricingTypes } from "../../../common/constants"
import { sum } from "../../../helpers/array"
import { ApprovedPaymentSummary, ApprovedQuotedGroupAmount } from "../../../domain/proposals/paymentSummary"
import { InvoiceWorkareaGroup } from "./createInvoiceModel"

export const getQuotedPriceTotalAmount = (workareaGroups: InvoiceWorkareaGroup[]): number => {
  return sum(workareaGroups.filter(x => x.selected && x.pricingType === PricingTypes.QuotedPrice), x => x.amount)
}

export const getQuotedPriceTotalUnbilledAmount = (workareaGroups: InvoiceWorkareaGroup[]): number => {
  return sum(workareaGroups.filter(x => x.selected && x.pricingType === PricingTypes.QuotedPrice), x => x.unbilledAmount)
}

export const getQuotedPriceTotalBillableAmount = (workareaGroups: InvoiceWorkareaGroup[]): number => {
  return sum(workareaGroups.filter(x => x.selected && x.pricingType === PricingTypes.QuotedPrice), x => x.billableAmount)
}

export type CalculatedWorkareaAmount = {
  groupId: string
  workareaId?: number
  amount: number
  maxBillableAmount: number
}

export const calculateNewAmountsFromPercentageInput = (workareaGroups: InvoiceWorkareaGroup[], percentage: number): CalculatedWorkareaAmount[] => {
  const selectedGroups = workareaGroups.filter(x => x.selected && x.pricingType === PricingTypes.QuotedPrice)

  const newAmounts: CalculatedWorkareaAmount[] = []
  selectedGroups.forEach(group => {
    if (group.workareas.length > 0) {
      group.workareas.forEach(x => {
        newAmounts.push({
          groupId: group.id,
          workareaId: x.workareaId,
          amount: Math.min(Math.round(x.quotedPrice * percentage), x.maxBillableAmount),
          maxBillableAmount: x.maxBillableAmount
        })
      })
    } else {
      newAmounts.push({
        groupId: group.id,
        amount: Math.min(Math.round(group.billableAmount * percentage), group.unbilledAmount),
        maxBillableAmount: group.unbilledAmount
      })
    }
  })
  return newAmounts
}

export const calculateNewAmountsFromTotalDollarInput = (workareaGroups: InvoiceWorkareaGroup[], total: number): CalculatedWorkareaAmount[] => {
  const totalBillable = getQuotedPriceTotalBillableAmount(workareaGroups)
  if (totalBillable <= 0) {
    return []
  }
  const percentage = total / totalBillable
  const newAmounts = calculateNewAmountsFromPercentageInput(workareaGroups, percentage)

  return adjustAmountsToMatchTotal(newAmounts, total)
}

const adjustAmountsToMatchTotal = (newAmounts: CalculatedWorkareaAmount[], total: number): CalculatedWorkareaAmount[] => {
  const initialSum = sum(newAmounts, x => x.amount)
  if (initialSum > total) {
    return reduceAmountsUntilMatchingTotal(newAmounts, initialSum, total)
  }
  if (initialSum < total) {
    return increaseAmountsUntilMatchingTotal(newAmounts, initialSum, total)
  }
  return newAmounts
}

const reduceAmountsUntilMatchingTotal = (workareaAmounts: CalculatedWorkareaAmount[], initialSum: number, total: number): CalculatedWorkareaAmount[] => {
  let remaining = initialSum - total
  return workareaAmounts.map(x => {
    const amountToApply = Math.min(remaining, x.amount)
    remaining -= amountToApply
    return {...x , amount: x.amount - amountToApply }
  })
}

const increaseAmountsUntilMatchingTotal = (workareaAmounts: CalculatedWorkareaAmount[], initialSum: number, total: number): CalculatedWorkareaAmount[] => {
  let remaining = total - initialSum
  return workareaAmounts.map(x => {
    const amountToApply = Math.min(remaining, x.maxBillableAmount - x.amount)
    remaining -= amountToApply
    return {...x , amount: x.amount + amountToApply }
  })
}

export const calculateNewAmountsFromApprovedPayment = (workareaGroups: InvoiceWorkareaGroup[], groupAmount: ApprovedQuotedGroupAmount): CalculatedWorkareaAmount[] => {
  const group = workareaGroups.find(x => x.id === groupAmount.workareaGroupId)
  if (!group) {
    return []
  }

  if (group.workareas.length > 0) {
    const percent = groupAmount.amount / group.billableAmount
    const newAmounts = group.workareas.map(x => ({
        groupId: group.id,
        workareaId: x.workareaId,
        amount: Math.min(Math.round(x.quotedPrice * percent), x.maxBillableAmount),
        maxBillableAmount: x.maxBillableAmount
    }))

    return adjustAmountsToMatchTotal(newAmounts, groupAmount.amount)
  }
  return [
    {
      groupId: group.id,
      amount: Math.min(groupAmount.amount, group.unbilledAmount),
      maxBillableAmount: group.unbilledAmount
    }
  ]
}

export type ApprovedPaymentOption = {
  value: string
  isApprovedGroup: boolean
  approvedAt?: string
  name: string
  quotedPriceAmount: number
  percentage?: number
  quotedGroups: ApprovedQuotedGroupAmount[]
}

export const getApprovedPaymentOptions = (summaries: ApprovedPaymentSummary[], workareaGroups: InvoiceWorkareaGroup[]): ApprovedPaymentOption[] => {
  const results: ApprovedPaymentOption[] = []
  summaries.forEach(summary => {
    if (summary.progressBased) {
      if (summary.progressBased.workareaGroupIds.some(id => workareaGroups.some(x => x.id === id))) {
        results.push(createGroupApprovedPaymentOption(summary))
        results.push(...createProgressBasedApprovedPaymentOptions(summary))
      }
    }
    if (summary.intervalBased) {
      if (summary.intervalBased.workareaGroupIds.some(id => workareaGroups.some(x => x.id === id))) {
        results.push(createGroupApprovedPaymentOption(summary))
        results.push(createIntervalBasedApprovedPaymentOption(summary))
      }
    }
  })

  return results
}

const createGroupApprovedPaymentOption = (summary: ApprovedPaymentSummary): ApprovedPaymentOption => {
  return {
    value: `${summary.proposalId}-group`,
    isApprovedGroup: true,
    approvedAt: summary.approvedAt,
    name: '',
    quotedPriceAmount: 0,
    percentage: 0,
    quotedGroups: []
  }
}

const createProgressBasedApprovedPaymentOptions = (summary: ApprovedPaymentSummary): ApprovedPaymentOption[] => {
  return summary.progressBased!.payments.map((payment, index) => {
    const quotedPriceAmount = sum(payment.quotedGroups, x => x.amount)
    return {
      value: `${summary.proposalId}-progress-${index}`,
      isApprovedGroup: false,
      name: payment.title,
      approvedAt: summary.approvedAt,
      quotedPriceAmount,
      percentage: payment.percentage,
      quotedGroups: payment.quotedGroups
    }
  })
}

const createIntervalBasedApprovedPaymentOption = (summary: ApprovedPaymentSummary): ApprovedPaymentOption => {
  const quotedPriceAmount = sum(summary.intervalBased!.quotedGroups, x => x.amount)
  return {
    value: `${summary.proposalId}-interval-based`,
    isApprovedGroup: false,
    name: `Interval payment`,
    approvedAt: summary.approvedAt,
    quotedPriceAmount,
    quotedGroups: summary.intervalBased!.quotedGroups
  }
}